import { DAY_IN_MS } from 'constants/date';

export const xTicksFromRange = (times: Date[], count = 5): Date[] => {
    // Generate "count" equispaced ticks between the range, including the endpoints
    if (times.length === 0) return [];

    const sampledTimes = [times[0]];

    for (let i = 1; i <= count - 1; i++) {
        const index = Math.floor(((times.length - 1) / (count - 1)) * i);
        sampledTimes.push(times[index]);
    }

    return sampledTimes;
};

// Used for the axis labels (less detailed)
export const xAxisTickFormatFromRange = (range: [Date, Date], isDailyOnly = false): Intl.DateTimeFormatOptions => {
    const daysBetween = Math.round((range[1].getTime() - range[0].getTime()) / DAY_IN_MS);

    if (daysBetween < 2) {
        return isDailyOnly ? { day: 'numeric', month: 'short' } : { hour: 'numeric', minute: 'numeric' };
    }

    if (daysBetween < 60) {
        return { day: 'numeric', month: 'short' };
    }

    if (daysBetween < 365) {
        return { month: 'short' };
    }

    if (daysBetween < 365 * 3) {
        return { month: 'short', year: 'numeric' };
    }

    return { year: 'numeric' };
};

// Used for hover states (more detailed)
export const xTickFormatFromRange = (
    range: [Date, Date],
    granularitySeconds?: number,
    isDailyOnly = false,
): Intl.DateTimeFormatOptions => {
    const daysBetween = Math.round((range[1].getTime() - range[0].getTime()) / DAY_IN_MS);
    const isSubDailyData = !isDailyOnly && granularitySeconds !== undefined && granularitySeconds < DAY_IN_MS / 1000;

    if (daysBetween < 3) {
        if (isDailyOnly) return { day: 'numeric', month: 'short' };
        return { hour: 'numeric', minute: 'numeric' };
    }

    if (daysBetween < 60) {
        if (isDailyOnly) return { day: 'numeric', month: 'short' };
        return { day: 'numeric', month: 'short', hour: 'numeric' };
    }

    if (daysBetween < 365) {
        if (isSubDailyData) return { month: 'short', day: '2-digit', hour: 'numeric' };
        return { month: 'short', day: '2-digit' };
    }

    if (daysBetween < 365 * 3) {
        if (isSubDailyData) return { month: 'short', year: 'numeric', day: '2-digit', hour: 'numeric' };
        return { month: 'short', year: 'numeric', day: '2-digit' };
    }

    return { month: 'short', year: 'numeric' };
};

export const yTicksFromRangeAndCount = (min: number, max: number, count: number, maxTick?: number): number[] => {
    if (count < 3) return [min, max];
    if (min >= max) return [min, max];

    const maxStep = (max - min) / (count - (count > 3 ? 3 : 2));
    const minStep = (max - min) / (count - 1);

    // Flooring the low tick quantizes which interval counts are reachable, so for some
    // ranges (typically crossing zero, e.g. [-50, 25]) no step hits `count - 1` exactly.
    // Keep the widest step that still spans [min, max] as a fallback so those ranges get
    // real ticks instead of the bare endpoints.
    let fallbackTicks: number[] | undefined;
    let fallbackIntervals = 0;

    // Look for values between min and max step (exclusive of min step)
    const powerOfTen = Math.floor(Math.log10(maxStep));
    // Candidate steps always land in (minStep, maxStep); smaller base steps only sample that
    // window more finely. Stop a few decades down — sampling it to ~0.1% is plenty, and going
    // to 1e-9 costs billions of iterations (a frozen graph) whenever nothing matches exactly.
    const smallestPowerOfTen = Math.max(powerOfTen - 3, -10);
    for (let i = powerOfTen; i >= smallestPowerOfTen; i--) {
        const preferredMultiples = [10, 5, 2.5, 2];
        for (const multiple of preferredMultiples) {
            const baseStep = multiple * 10 ** i;

            let multipleOfStep = Math.floor(minStep / baseStep) * baseStep;

            while (multipleOfStep + baseStep < maxStep) {
                multipleOfStep += baseStep;

                // Math.floor keeps the low tick at or below min for negative values,
                // where `min - (min % step)` would round up toward 0
                const lowVal = Math.floor(min / multipleOfStep) * multipleOfStep;
                const intervals = Math.ceil((max - lowVal) / multipleOfStep);
                if (intervals > count - 1) {
                    // `count` ticks of this step would stop short of max
                    continue;
                }

                const proposedTicks = Array.from({ length: count }, (_, i) => lowVal + i * multipleOfStep);
                if (maxTick && proposedTicks[proposedTicks.length - 1] > maxTick) {
                    continue;
                }

                if (intervals === count - 1) {
                    return proposedTicks;
                }

                if (intervals > fallbackIntervals) {
                    fallbackIntervals = intervals;
                    fallbackTicks = proposedTicks;
                }
            }
        }
    }

    return fallbackTicks ?? [min, max];
};
